import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from 'aws-blocks';
import { useAuthContext } from '../../contexts/AuthContext';
import ProfileAvatar from '../../Components/ProfileAvatar';
import BkPage from '../../Components/ui/BkPage';
import BkPanel from '../../Components/ui/BkPanel';
import usePageMeta from '../../hooks/use-page-meta';
import { tierLabel } from '../../profile-frames/registry';

type LadderEntry = {
  rank: number;
  playerId: string;
  displayName: string;
  avatarId?: string;
  displayFrameId?: string;
  elo: number;
  tier: string;
  wins: number;
  losses: number;
};

type LeaderboardPayload = {
  seasonId: string;
  seasonName: string;
  startsAt: number;
  endsAt: number;
  entries: LadderEntry[];
  viewer: {
    rank: number | null;
    elo: number | null;
    tier?: string;
    wins?: number;
    losses?: number;
    displayFrameId?: string;
  } | null;
  seasons: Array<{
    seasonId: string;
    name: string;
    startsAt: number;
    endsAt: number;
  }>;
};

export default function Leaderboard() {
  usePageMeta({
    title: 'Leaderboard',
    description: 'Season ranked ladder — top Blind Kings competitors.',
    path: '/leaderboard',
  });

  const { currentUser, userData } = useAuthContext();
  const [data, setData] = useState<LeaderboardPayload | null>(null);
  const [seasonId, setSeasonId] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const result = (await api.getLeaderboard(
          seasonId,
          100
        )) as LeaderboardPayload;
        if (cancelled) return;
        setData(result);
        if (!seasonId) setSeasonId(result.seasonId);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [seasonId, currentUser?.userId]);

  const viewer = data?.viewer;
  const myName = userData?.displayName;

  return (
    <BkPage>
      <div className="mx-auto w-full max-w-3xl space-y-6 px-4 py-8 sm:px-6">
        <header className="space-y-2 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-bk-gold-dim">
            Ranked ladder
          </p>
          <h1 className="font-display text-3xl font-extrabold tracking-wide text-bk-text sm:text-4xl">
            Leaderboard
          </h1>
          <p className="text-sm text-bk-muted">
            {data?.seasonName
              ? `${data.seasonName} — climb the Crownfall ranks`
              : 'Season standings for ranked play'}
          </p>
        </header>

        {data?.seasons && data.seasons.length > 1 ? (
          <div className="flex flex-wrap justify-center gap-2">
            {data.seasons.map((s) => (
              <button
                key={s.seasonId}
                type="button"
                onClick={() => setSeasonId(s.seasonId)}
                className={[
                  'border px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors',
                  seasonId === s.seasonId
                    ? 'border-bk-gold bg-bk-gold/15 text-bk-gold'
                    : 'border-bk-border text-bk-muted hover:border-bk-gold-dim',
                ].join(' ')}
              >
                {s.name}
              </button>
            ))}
          </div>
        ) : null}

        {currentUser && viewer ? (
          <BkPanel className="flex flex-wrap items-center justify-between gap-3 border-bk-gold/50">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-bk-muted">
                Your standing
              </p>
              <p className="mt-1 text-lg font-extrabold text-bk-text">
                {viewer.rank != null ? `#${viewer.rank}` : 'Unranked'}
                <span className="ml-2 text-sm font-semibold text-bk-gold">
                  {tierLabel(viewer.tier)} · {viewer.elo ?? '—'}
                </span>
              </p>
              <p className="text-xs text-bk-muted">
                Season record {viewer.wins ?? 0}–{viewer.losses ?? 0}
                {myName ? ` · ${myName}` : ''}
              </p>
            </div>
            <Link
              to="/queue/ranked"
              className="border border-bk-gold bg-bk-gold/10 px-4 py-2 text-xs font-bold uppercase tracking-wider text-bk-gold hover:bg-bk-gold hover:text-bk-bg"
            >
              Queue ranked
            </Link>
          </BkPanel>
        ) : null}

        <BkPanel className="overflow-hidden p-0">
          {loading ? (
            <p className="px-4 py-10 text-center text-sm text-bk-muted">
              Loading ladder…
            </p>
          ) : error ? (
            <p className="px-4 py-10 text-center text-sm text-bk-danger">{error}</p>
          ) : !data?.entries?.length ? (
            <p className="px-4 py-10 text-center text-sm text-bk-muted">
              No ranked players on this ladder yet. Be the first.
            </p>
          ) : (
            <ul className="divide-y divide-bk-border">
              {data.entries.map((entry) => {
                const isYou =
                  !!currentUser &&
                  (entry.playerId === currentUser.userId ||
                    entry.displayName === myName);
                return (
                  <li
                    key={`${entry.rank}-${entry.playerId}`}
                    className={[
                      'flex items-center gap-3 px-3 py-3 sm:gap-4 sm:px-4',
                      isYou ? 'bg-bk-gold/10' : '',
                    ].join(' ')}
                  >
                    <span className="w-8 shrink-0 text-center text-sm font-extrabold tabular-nums text-bk-gold">
                      {entry.rank}
                    </span>
                    <ProfileAvatar
                      avatarId={entry.avatarId}
                      frameId={entry.displayFrameId}
                      size="sm"
                    />
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/user/${encodeURIComponent(entry.displayName)}`}
                        className="block truncate font-bold text-bk-text hover:text-bk-gold"
                      >
                        {entry.displayName}
                        {isYou ? (
                          <span className="ml-2 text-[10px] uppercase tracking-wider text-bk-gold">
                            You
                          </span>
                        ) : null}
                      </Link>
                      <p className="text-[11px] uppercase tracking-wider text-bk-muted">
                        {tierLabel(entry.tier)} · {entry.wins}–{entry.losses}
                      </p>
                    </div>
                    <span className="shrink-0 text-base font-extrabold tabular-nums text-bk-text">
                      {entry.elo}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </BkPanel>
      </div>
    </BkPage>
  );
}
